import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { fetchMe, isOtpRequired, login } from "@/api/auth";
import { loginOtpStep } from "@/api/twofa";
import EphemeralStorageBanner from "@/components/EphemeralStorageBanner";
import { useAuthStore } from "@/store/auth";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [otpToken, setOtpToken] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await login(username, password);
      if (isOtpRequired(result)) {
        // 2FA enabled — OTP step UI
        setOtpToken(result.otp_token);
      } else {
        setTokens(result.access_token, result.refresh_token);
        const me = await fetchMe();
        setUser(me);
        navigate("/", { replace: true });
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t("login.failed"));
    } finally {
      setLoading(false);
    }
  }

  async function onOtpSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!otpToken) return;
    setLoading(true);
    setError(null);
    try {
      const tokens = await loginOtpStep({ otp_token: otpToken, code: otpCode });
      setTokens(tokens.access_token, tokens.refresh_token);
      const me = await fetchMe();
      setUser(me);
      navigate("/", { replace: true });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t("login.twofa_failed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-4">
      <div className="w-full max-w-md bg-zinc-900 rounded-xl border border-zinc-800 p-8 shadow-2xl">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-white">{t("app.name")}</h1>
          <p className="text-zinc-400 mt-2">{t("app.title")}</p>
          <p className="text-xs text-zinc-500 mt-1">{t("app.subtitle")}</p>
        </div>
        {/* 데모 저장소 휘발 고지 (2026-07-16) — 로그인/가입 시점부터 정직 고지
            (Layout 배너는 로그인 후에만 보임 — 계정 생성 전 기대 관리) */}
        <EphemeralStorageBanner />

        {!otpToken ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-username" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{t("login.username")}</label>
              <input
                id="login-username"
                type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                autoComplete="username" required
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{t("login.password")}</label>
              <input
                id="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" required
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            {error && (
              <div className="text-sm text-sev-critical bg-red-950/40 border border-red-900 rounded-md p-2">
                {error}
              </div>
            )}
            <button
              type="submit" disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-medium py-2 rounded-md transition-colors"
            >
              {loading ? t("login.submitting") : t("login.submit")}
            </button>
          </form>
        ) : (
          <form onSubmit={onOtpSubmit} className="space-y-4">
            <div className="text-center">
              <p className="text-zinc-700 dark:text-zinc-300">{t("login.twofa_title")}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {t("login.twofa_hint")}
              </p>
            </div>
            <input
              type="text" inputMode="numeric" autoFocus
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/[^A-Z0-9]/gi, "").toUpperCase())}
              placeholder="123456 또는 ABCD1234"
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white text-center font-mono text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            {error && (
              <div className="text-sm text-sev-critical bg-red-950/40 border border-red-900 rounded-md p-2">
                {error}
              </div>
            )}
            <button
              type="submit" disabled={loading || otpCode.length < 6}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 text-white font-medium py-2 rounded-md transition-colors"
            >
              {loading ? t("login.twofa_submitting") : t("login.twofa_submit")}
            </button>
            <button
              type="button"
              onClick={() => { setOtpToken(null); setOtpCode(""); setError(null); }}
              className="w-full text-sm text-zinc-500 hover:underline"
            >
              {t("login.twofa_back")}
            </button>
          </form>
        )}

        <p className="text-xs text-zinc-500 text-center mt-6">
          {t("app.footer")}
        </p>
      </div>
    </div>
  );
}
